/* eslint class-methods-use-this: 0, no-param-reassign: 0 */
import { ImportUsersConsts } from 'client/util/Constants';
import { mixin } from 'lodash';
import { scimConnectionTypes } from 'client/util/Constants/ApplicationConstants';
import ApplicationUtils from 'client/applications/utils/ApplicationUtils';
import CollectionModule from 'client/store/modules/CollectionModule';
import ImportUsersMixin, {
  addIsNewFieldToUsers,
  filterItemsByField,
  searchProperty,
  updateState,
} from 'client/store/mixins/ImportUsersMixin';
import ScimUsersImportApi from 'client/api/v2/ScimUsersImportApi';
import Util from 'client/util/Util';

const { customRestAdditionalAttributes, scimUserAdditionalAttributes, source } = ImportUsersConsts;
const { CUSTOM, SCIM } = scimConnectionTypes;

const creationSourceMap = {
  [SCIM]: source.scim,
  [CUSTOM]: source.custom,
};

/**
 *
 * @param {Object} user User object coming from ImportCollection
 * @returns {Object} Object that has the keys specified by the
 * scimUserAdditionalAttributes constant array
 *
 * Return value example: { employeeType: 'Dev', costCenter: '' }
 */
export const attachExtraAttributes = (user) => {
  const requiredFields = ['manager', 'alternateEmail'];
  const outputObject = {};

  scimUserAdditionalAttributes.forEach((attribute) => {
    outputObject[attribute] = user[attribute];
  });

  customRestAdditionalAttributes.forEach((attribute) => {
    outputObject[attribute] = user[attribute];
    if (requiredFields.includes(attribute) && user[attribute] === '') {
      delete outputObject[attribute];
    }
  });

  return outputObject;
};

export const processUsers = users => users.map((user) => {
  let systemUser = {
    email: user.email,
    firstname: user.firstname,
    lastname: user.lastname,
    username: `${user.email.split('@')[0]}`,
  };

  const attributes = attachExtraAttributes(user);

  if (Object.keys(attributes).length !== 0) systemUser = { ...systemUser, ...attributes };

  return systemUser;
});

/**
 * Helper function to add a temp ID to retrieve users who don't have an
 * ID (no ID = [id === '' || id === undefined])
 * @param {Array} usersArray Array of users
 * @returns Same array of passed users but with temp id attached
 */
const attachTempId = (usersArray) => usersArray.map(user => {
  if (!user.id) {
    user.id = Util.getUniqueId();
  }

  return user;
});

export class ScimUsersImportCollectionClass extends CollectionModule {
  /**
   * Override method from base class to add scim specific fields to the Vuex
   * module entities and features.
   */
  extractModule() {
    const module = super.extractModule();

    mixin(module.actions, ImportUsersMixin.actions);
    mixin(module.getters, ImportUsersMixin.getters);
    mixin(module.mutations, ImportUsersMixin.mutations);

    module.actions.addIsNewFieldToUsers = (...params) => this.addIsNewFieldToUsers(...params);
    module.actions.fetchCollection = (...params) => this.fetchCollection(...params);
    module.actions.handlePaginationSkip = (...params) => this.handlePaginationSkip(...params);
    module.actions.importSelectedUsers = (...params) => this.importSelectedUsers(...params);
    module.actions.importUsers = (...params) => this.importUsers(...params);
    module.actions.setApplicationConnectionType = (...params) => this
      .setApplicationConnectionType(...params);
    module.actions.setBulkImportLimit = (...params) => this.setBulkImportLimit(...params);
    module.actions.setScimApplicationId = (...params) => this.setScimApplicationId(...params);
    module.actions.setScimApplicationSourceName = (...params) => this
      .setScimApplicationSourceName(...params);
    module.actions.setUsersFilterField = (...params) => this.setUsersFilterField(...params);
    module.actions.setImportInjectionQuery = (...params) => this.setImportInjectionQuery(...params);

    module.getters.getErrorPollingImportResults = (...params) => this
      .getErrorPollingImportResults(...params);
    module.getters.getUsersTotalCount = (...params) => this
      .getUsersTotalCount(...params);
    module.getters.getImportInjectionQuery = (...params) => this
      .getImportInjectionQuery(...params);

    module.mutations.SET_APP_CONNECTION_TYPE = (...params) => this
      .SET_APP_CONNECTION_TYPE(...params);
    module.mutations.SET_BULK_IMPORT_LIMIT = (...params) => this.SET_BULK_IMPORT_LIMIT(...params);
    module.mutations.SET_SCIM_APP_ID = (...params) => this.SET_SCIM_APP_ID(...params);
    module.mutations.SET_SCIM_APP_SOURCE_NAME = (...params) => this
      .SET_SCIM_APP_SOURCE_NAME(...params);
    module.mutations.SET_USERS_FILTER_FIELD = (...params) => this.SET_USERS_FILTER_FIELD(...params);
    module.mutations.SET_IMPORT_INJECTION_QUERY = (...params) => this
      .SET_IMPORT_INJECTION_QUERY(...params);

    return module;
  }

  // ----------------------------------------------------------------------------------------------
  // State
  // ----------------------------------------------------------------------------------------------

  getDefaultStateFeatures() {
    return {
      bulkImportLimit: 100,
      importInjectionQuery: '',
    };
  }

  getInitialState() {
    const state = updateState(super.getInitialState());
    state.features = {
      ...state.features,
      ...this.getDefaultStateFeatures(),
    };
    return state;
  }

  // ----------------------------------------------------------------------------------------------
  // Actions
  // ----------------------------------------------------------------------------------------------

  handlePaginationSkip(context, options) {
    let { skip } = context.state.features.items.pagination;
    const { limit } = context.state.features.items.pagination;
    const { scimAppSourceName } = context.state.entities;
    const { next } = options;

    const doesAppStartWithIndexOne = () => {
      const appSupportsIndexOne = ApplicationUtils.appStartIndexOne().find(
        app => app === scimAppSourceName,
      );
      return (appSupportsIndexOne && !next && skip === 0);
    };

    // apps that don't support startIndex=0
    if (doesAppStartWithIndexOne()) {
      skip = 1;
      context.commit('SET_PAGINATION_SKIP', { skip });
    } else if (next) {
      skip += limit;
      if (skip < context.state.entities.items.totalCount) {
        context.commit('SET_PAGINATION_SKIP', { skip });
      }
    }
  }

  /**
   * Override CollectionModule fetchCollection method
   */
  async fetchCollection(context, { next = false } = {}) {
    this.handlePaginationSkip(context, { next });

    const { sort, pagination } = context.state.features.items;
    const { limit, skip } = pagination;

    try {
      // Idm apps searchTerm, filters, nextPageToken, skipToken are not used nor changed.
      const items = await this.fetchWrapper(context, skip, limit, sort, null,
        [], null, null);
      let scimApplicationUser;
      let identifierProperty;
      if (Array.isArray(items.results) && items.results.length) {
        scimApplicationUser = items.results[0].email;

        // Add computed isNew field
        identifierProperty = searchProperty(items.results[0]);
        items.results = await addIsNewFieldToUsers(items.results, identifierProperty);
        items.results = attachTempId(items.results);
      }

      let payload = { totalCount: 0, results: [] };
      if (scimApplicationUser) {
        const { filter } = context.state.entities;
        const currentTotalCount = context.state.entities.items.totalCount
          ? context.state.entities.items.totalCount : items.totalCount;
        let userCountDifference = items.results.length;
        if (filter) {
          // Filter key and filter value
          const {
            key = '',
            value = '',
          } = context.state.entities.filter;
          items.results = filterItemsByField(items.results, key, value);
          userCountDifference -= items.results.length;
        }

        const totalCountDiff = (currentTotalCount - userCountDifference);
        /**
         * If totalCountDiff equals 0 then it means that no users were filtered
         * but we can't pass 0 to the payload.totalCount because this value will reach
         * the ItemTable and some expressions make use of this (Ex. showLoadingIndicator).
         *
         * So we evaluate this here
         */
        const updatedTotalCount = totalCountDiff === 0 ? items.totalCount : totalCountDiff;

        // This length variable is used temporarily, SCIM BE has plans to do the same logic
        // @todo: Remove this when the BE implements this logic so that the fetchCollection
        // actions is more aligned with the rest of the other user import's fetchCollection
        const useArrayLength = (updatedTotalCount === 0 && items.results.length !== 0);

        payload = {
          results: items.results,
          totalCount: useArrayLength ? items.results.length : updatedTotalCount,
          totalCountOfAllItems: items.totalCount,
        };
      }

      if (context.state.entities.items.totalCountOfAllItems === null) {
        payload.totalCountOfAllItems = payload.totalCount;
      }

      context.commit('SET_COLLECTION', payload);

      return items.results;
    } catch (err) {
      if (err && err.status && err.status < 500) {
        context.commit('SET_IMPORT_ERROR', err.message);
        return [];
      }

      return Promise.reject(err);
    }
  }

  addIsNewFieldToUsers(ctx, { users, emailField }) {
    return addIsNewFieldToUsers(users, emailField);
  }

  /**
   * Override method from ImportUsersMixin to specify a custom method to process the users prior to
   * importing them into JumpCloud.
   */
  importSelectedUsers(context) {
    const { appConnectionType, boundItems, scimAppSourceName } = context.state.entities;
    const creationSource = creationSourceMap[appConnectionType];
    const sourceHeader = {
      creationSource,
      creationSourceName: scimAppSourceName,
    };

    const byIdKeys = Object.keys(boundItems.byId);
    byIdKeys.forEach((key) => {
      const user = boundItems.byId[key];
      delete user.selected;
    });

    return ImportUsersMixin.actions.importSelectedUsers(
      context, processUsers, sourceHeader,
    );
  }

  importUsers({ commit }, { itemId, queryString, importCase }) {
    return ImportUsersMixin.actions.importUpdateUsers(
      { commit },
      {
        id: itemId,
        queryString,
        operations: importCase,
      },
    );
  }

  setApplicationConnectionType({ commit }, { appConnectionType }) {
    commit('SET_APP_CONNECTION_TYPE', appConnectionType);
  }

  setBulkImportLimit({ commit }, bulkImportLimit) {
    commit('SET_BULK_IMPORT_LIMIT', bulkImportLimit);
  }

  setScimApplicationId({ commit }, { scimAppId }) {
    commit('SET_SCIM_APP_ID', { scimAppId });
  }

  setUsersFilterField({ commit }, { key, value }) {
    commit('SET_USERS_FILTER_FIELD', { key, value });
  }

  setScimApplicationSourceName({ commit }, { scimAppSourceName }) {
    commit('SET_SCIM_APP_SOURCE_NAME', { scimAppSourceName });
  }

  setImportInjectionQuery({ commit }, query) {
    commit('SET_IMPORT_INJECTION_QUERY', query);
  }

  // ----------------------------------------------------------------------------------------------
  // Getters
  // ----------------------------------------------------------------------------------------------

  getErrorPollingImportResults(state = { features: {} }) {
    return state.features.errorPollingImportResults;
  }

  getUsersTotalCount(state = { entities: { items: { totalCount: 0 } } }) {
    return state.entities.items.totalCount;
  }

  getImportInjectionQuery(state = { entities: { items: { importInjectionQuery: '' } } }) {
    return state.features.importInjectionQuery;
  }
  // ----------------------------------------------------------------------------------------------
  // Mutations
  // ----------------------------------------------------------------------------------------------

  /**
   * When sorting or searching a soft reset is performed. This preserves the
   * selected and modified items. This allows the admin user's selection and
   * changes to persist, and not be lost, when searching and sorting.
   */
  RESET_COLLECTION(state = {
    features: {},
    entities: {},
  }, { soft = false } = {}) {
    super.RESET_COLLECTION(state, { soft });
    const initialState = this.getInitialState();
    state.entities.scimAppId = initialState.entities.scimAppId;
    state.features.jobId = initialState.features.jobId;
  }

  SET_APP_CONNECTION_TYPE(state, appConnectionType) {
    state.entities.appConnectionType = appConnectionType;
    this.Api.setApplicationConnectionType(appConnectionType);
  }

  SET_BULK_IMPORT_LIMIT(state = { features: {} }, bulkImportLimit = false) {
    state.features.bulkImportLimit = bulkImportLimit;
  }

  SET_SCIM_APP_ID(_state, { scimAppId }) {
    this.Api.setScimApplicationId(scimAppId);
  }

  SET_SCIM_APP_SOURCE_NAME(state = { entities: { scimAppSourceName: {} } }, { scimAppSourceName }) {
    state.entities.scimAppSourceName = scimAppSourceName;
    this.Api.setScimApplicationSourceName(scimAppSourceName);
  }

  SET_USERS_FILTER_FIELD(state = { entities: { filter: {} } }, { key = '', value = '' }) {
    state.entities.filter = { key, value };
  }

  SET_IMPORT_INJECTION_QUERY(state = { features: {} }, query) {
    state.features.importInjectionQuery = query;
    this.Api.setImportInjectionQuery(query);
  }
}

export default new ScimUsersImportCollectionClass(ScimUsersImportApi, 'id', '').extractModule();
